import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { ReconciliationSupplierDetail, UpdateCommissionAdjustmentPayload } from './types';

const getEndpoint = (supplierId: number) => `ums/v1/cms/reconciliation/suppliers/${supplierId}/commission-adjustment`;

type TData = ReconciliationSupplierDetail;
type TError = AxiosError;
type TVariables = UpdateCommissionAdjustmentPayload;
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

/** Month-level commission waiver / signed adjustment for a supplier — never touches per-order terms
 * or invoices. Returns the refreshed detail (bare, like the collection PATCH). 409 when closed. */
export function useUpdateCommissionAdjustmentApi(options?: Options): ReturnType {
  const toast = useCustomToast();

  const mutationFn = async ({ month, supplierId, waived, adjustment, reason }: TVariables) => {
    try {
      const { data } = await http.patch(
        getEndpoint(supplierId),
        { waived, adjustment: adjustment ?? null, reason: reason ?? null },
        { params: { month } }
      );
      return data;
    } catch (error: any) {
      if (!error.response?.status) throw error;

      toast({
        description: error.response?.data?.message ?? 'Failed to save the commission adjustment',
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { ...options, mutationKey: ['reconciliation-commission-adjustment'] });
}
